import { createRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import LoadingSpinner from '@/components/ui/loading-spinner';
import LoadingLinear from '@/components/ui/loading-linear';

export type LoaderRefType = {
  incLoader: () => void;
  decLoader: () => void;
};

export const loaderRef = createRef<LoaderRefType>();

interface ILoader {
  className?: string;
  enableScroll?: boolean;
  linearItemClassName?: string;
  variant?: 'circular' | 'linear';
}

const Loader: React.FC<ILoader> = ({ className, enableScroll = false, linearItemClassName, variant = 'circular' }) => {
  const [counter, setCounter] = useState(0);
  const incLoader = () => {
    setCounter(x => x + 1);
  };

  const decLoader = () => {
    setCounter(x => x - 1)
  };

  useImperativeHandle(loaderRef, () => ({
    incLoader,
    decLoader,
  }), []);

  const bodyPaddingRightRef = useRef<string | null>(null);
  const lockedRef = useRef(false);

  // Bu işlemler scroll barı gizlerken ekranın kaymamasını sağlamak için yapıldı
  useEffect(() => {
    const body = document.body;

    const lockScroll = () => {
      if (lockedRef.current) return;
      const computed = window.getComputedStyle(body).paddingRight || '0px';
      bodyPaddingRightRef.current = computed;

      const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
      if (scrollBarWidth > 0) {
        const prev = parseFloat(computed) || 0;
        body.style.paddingRight = `${prev + scrollBarWidth}px`;
      }

      body.classList.add('overflow-hidden');
      lockedRef.current = true;
    };

    const unlockScroll = () => {
      if (!lockedRef.current) return;
      if (bodyPaddingRightRef.current !== null) {
        body.style.paddingRight = bodyPaddingRightRef.current;
      } else {
        body.style.removeProperty('padding-right');
      }
      body.classList.remove('overflow-hidden');
      lockedRef.current = false;
    };

    if (!enableScroll && counter > 0) {
      lockScroll();
    } else {
      unlockScroll();
    }

    return () => {
      if (lockedRef.current) {
        if (bodyPaddingRightRef.current !== null) {
          body.style.paddingRight = bodyPaddingRightRef.current;
        } else {
          body.style.removeProperty('padding-right');
        }
        body.classList.remove('overflow-hidden');
        lockedRef.current = false;
      }
    };
  }, [counter, enableScroll]);

  return (
    counter > 0 && (
      <>
        {variant === 'circular'
          ? (
            <div className="z-9999 fixed left-0 top-0 flex h-screen w-screen items-center justify-center backdrop-brightness-50 dark:backdrop-brightness-75">
              <LoadingSpinner className={className} />
            </div>
          )
          : (
            <div className="z-9999 fixed left-0 top-0 h-screen w-screen backdrop-brightness-50 dark:backdrop-brightness-75">
              <LoadingLinear linearContainerClassName={className} linearItemClassName={linearItemClassName} />
            </div>
          )}
      </>
    )
  );
};

export default Loader;